import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@sf/ui';
import { useApi } from '../api/ApiProvider';

/**
 * What the demo account is told when it tries to change something.
 *
 * The server refuses every write from a demo session with a 403 and the code `demo_read_only`, and the
 * API client announces those (and only those) here. One toast, in the reader's own language, and
 * nothing else: no sign-out, no redirect, no page full of red — the visitor pressed a button the demo
 * cannot honour, which is a sentence, not an incident. The id keeps a form that fires several writes
 * to a single toast rather than a stack of identical ones.
 *
 * Mounted once, next to the toast provider, so it works on every page including the ones whose own
 * error handling would otherwise say nothing.
 */
export function DemoReadOnlyToaster() {
  const api = useApi();
  const toast = useToast();
  const { t } = useTranslation();

  // Read through refs so the subscription is made once: the toast context takes a new identity every
  // time a toast is shown, and this listener has no reason to be torn down and rebuilt with it.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(
    () =>
      api.onDemoReadOnly(() => {
        toastRef.current.show({
          id: 'demo-read-only',
          tone: 'warning',
          title: tRef.current('errors.demo_read_only'),
        });
      }),
    [api],
  );

  return null;
}
